'use client';

import React from 'react';
import { AmberPill } from '@loci/ui';
import type { EventDetailResponse } from '@/types/eventDetail';
import { CategoryIcon } from '@/components/atoms/CategoryIcon';
import { MissingDataNotice } from '@/components/molecules/MissingDataNotice';
import { cn } from '@/components/atoms/primitives/utils';
import { useStartsIn } from '@/hooks/useStartsIn';

export interface EventTitleBlockProps {
  event: EventDetailResponse;
  /**
   * Optional pre-formatted walk distance (e.g. `12 MIN WALK`). Hidden when
   * undefined. Callers provide this from geolocation-derived data; the
   * component stays dumb about location math.
   */
  distance?: string;
  /**
   * When true, renders the line-through strikethrough on the title — used
   * for CANCELLED events to match the hero status pill.
   */
  strikethrough?: boolean;
}

/**
 * LOCI title block — overlays the bottom of {@link EventDetailHero}.
 *
 * Row 1: category pill + amber "starts in" pill (hidden when past start)
 * Row 2: uppercase h1 title (Space Grotesk)
 * Row 3: venue · mono distance (distance hidden when absent)
 *
 * The component is a client component because it calls {@link useStartsIn}
 * for the live countdown label. See FRE-753.
 */
export function EventTitleBlock({ event, distance, strikethrough = false }: EventTitleBlockProps) {
  const { title, date, categories, location } = event;
  const startsInLabel = useStartsIn(date);
  const primaryCategory = categories[0] ?? null;

  return (
    <div
      data-testid="event-title-block"
      className="container mx-auto absolute bottom-0 left-0 right-0 px-4 tablet:px-6 desktop:px-8 pb-8 flex flex-col gap-3"
    >
      {(primaryCategory || startsInLabel) && (
        <div data-testid="title-block-pills" className="flex flex-wrap items-center gap-2">
          {primaryCategory && (
            <span
              data-testid="category-pill"
              style={
                primaryCategory.color
                  ? ({ '--badge-color': `${primaryCategory.color}CC` } as React.CSSProperties)
                  : undefined
              }
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium text-white backdrop-blur-sm',
                primaryCategory.color ? 'bg-[var(--badge-color)]' : 'bg-white/20',
              )}
            >
              {primaryCategory.iconName && (
                <CategoryIcon
                  iconName={primaryCategory.iconName}
                  size={12}
                  className="text-white"
                />
              )}
              {primaryCategory.name}
            </span>
          )}
          {startsInLabel && (
            <AmberPill data-testid="starts-in-pill" aria-live="polite">
              {startsInLabel}
            </AmberPill>
          )}
        </div>
      )}

      <h1
        className={cn(
          'font-display text-3xl tablet:text-4xl desktop:text-5xl font-bold uppercase tracking-tight text-white line-clamp-2',
          strikethrough && 'line-through',
        )}
      >
        {title}
      </h1>

      <div
        data-testid="title-block-venue-row"
        className="font-mono text-sm text-white/80 flex flex-wrap items-center gap-x-2 gap-y-1"
      >
        {location ? (
          <span>{location.name}</span>
        ) : (
          <MissingDataNotice variant="inline" field="Venue" className="text-white/70" />
        )}
        {distance && (
          <>
            <span aria-hidden="true">·</span>
            <span data-testid="title-block-distance">{distance}</span>
          </>
        )}
      </div>
    </div>
  );
}
